import { jsx } from 'react/jsx-runtime';
import * as React from 'react';
import { twMerge } from '@belong/common/twMerge';
import { useLink } from '@belong/common/useLink';
import { useTracking } from '@belong/common/useTracking';
import css from './LinkBase.module.css.js';

function getIsExternal(to) {
  return to.includes('://') || to.includes('mailto:') || to.includes('tel:');
}
function buildHrefFromObject(hrefObject) {
  const {
    pathname,
    search,
    hash
  } = hrefObject;
  let href = pathname;
  if (search) {
    href = `${href}?${search}`;
  }
  if (hash) {
    href = `${href}#${hash}`;
  }
  return href;
}
const LinkBase = /*#__PURE__*/React.forwardRef(function LinkBase({
  id,
  name,
  className,
  href,
  children,
  shallow,
  scroll,
  trackingData = {},
  onClick,
  onSelect,
  external,
  prefetch = false,
  ...rest
}, ref) {
  const {
    Link,
    baseHref
  } = useLink();
  const track = useTracking();
  const isExternal = typeof href === 'string' && (external || getIsExternal(href));
  function handleClick(event) {
    const _trackingData = {
      ...trackingData
    };
    if (event?.target) {
      // @ts-ignore
      _trackingData.text = event.target.innerText;
    }
    _trackingData.href = href;
    if (typeof href !== 'string') {
      _trackingData.href = buildHrefFromObject(href);
    }
    _trackingData.component = 'link';
    if (rest.role === 'tab') {
      _trackingData.component = 'tab';
    }
    track('click', {
      name: id || name,
      isExternal,
      ..._trackingData
    });
    if (onSelect) return onSelect(event);
    if (onClick) {
      return onClick(event);
    }
  }
  const anchorProps = {
    // Find out why outline-none here, causes an outline in consumer?
    className: twMerge('p-0 border-none cursor-pointer bg-none', css.linkBase, className),
    onClick: handleClick,
    onSelect: handleClick,
    ...rest,
    ref
  };
  if (isExternal) {
    return jsx("a", {
      href: href,
      ...anchorProps,
      children: children
    });
  }
  // Next Link needs an <a> tag to work, but React Router Link will give a warning, so we return without the <a>.
  // Now this is here for legacy reasons, we should probably rename to isWeb or something.
  if (Link.withoutAnchor) {
    return jsx(Link, {
      id: id,
      href: href,
      scroll: scroll,
      ...anchorProps,
      children: children
    });
  }
  return jsx(Link, {
    id: id,
    href: baseHref ? `${baseHref}${href}` : href,
    scroll: scroll,
    shallow: shallow,
    prefetch: prefetch,
    ...anchorProps,
    children: children
  });
});

export { LinkBase };
